/**
 * Which bulk actions a workspace selection allows, and why not when it
 * doesn't. Pure and shared so the frontend toolbar, context menus and the MCP
 * bulk tools all read one rule set.
 *
 * Reasons name the rule that failed and how to fix it. Entity words come from
 * `labels`, keyed by file kind, so the user-facing rename (collection to
 * folder) changes the defaults below and never the rules.
 */
import { BULK_DELETE_MAX_IDS } from '../constants/bulk';
export const DEFAULT_SELECTION_KIND_LABELS = {
    folders: 'collection',
    notes: 'note',
    protocols: 'protocol',
    spreadsheets: 'spreadsheet',
    documents: 'document',
    presentations: 'presentation',
    images: 'image',
    video: 'video',
    audio: 'audio file',
    instrument: 'instrument data file',
    code: 'code file',
    archives: 'archive',
    equipment: 'equipment item',
    links: 'link',
    other: 'file',
};
export const MERGE_MIN_NOTES = 2;
export const MERGE_MAX_NOTES = 20;
/** The merge rule on its own, for callers that only know counts. */
export function mergeRuleFailure(count, hasNonNotes) {
    if (hasNonNotes)
        return 'not-notes';
    if (count < MERGE_MIN_NOTES)
        return 'too-few';
    if (count > MERGE_MAX_NOTES)
        return 'too-many';
    return null;
}
const isNote = (item) => item.kind === 'file' && item.artifactType === 'note';
/** An uploaded file: the only artifact type with bytes to download. */
const isUpload = (item) => item.kind === 'file' && item.artifactType === 'attachment';
export function resolveSelectionActions(items, labels = {}) {
    const ids = [
        'open',
        'move',
        'copy',
        'merge',
        'favorite',
        'download',
        'delete',
        'rename',
    ];
    if (items.length === 0) {
        return ids.map((id) => ({
            id,
            enabled: false,
            reason: 'Select at least one item.',
            reasonCode: 'no_selection',
        }));
    }
    const words = { ...DEFAULT_SELECTION_KIND_LABELS, ...labels };
    const describe = (item) => {
        const word = words[item.kind === 'folder' ? 'folders' : item.fileKind];
        return `${item.name} is ${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}.`;
    };
    const count = items.length;
    const single = count === 1;
    const readOnly = items.find((item) => !item.canEdit);
    const oneAtATime = (verb) => single
        ? undefined
        : `${verb[0]?.toUpperCase()}${verb.slice(1)} works on one item at a time. Select just one to ${verb} it.`;
    const editReason = (verb) => readOnly &&
        `You can't ${verb} ${readOnly.name} because you only have view access. ${single ? `Ask someone with edit access to ${verb} it.` : `Deselect it to ${verb} the rest.`}`;
    const capReason = (verb) => count > BULK_DELETE_MAX_IDS
        ? `You can ${verb} up to ${BULK_DELETE_MAX_IDS} items at a time. Deselect ${count - BULK_DELETE_MAX_IDS} to continue.`
        : undefined;
    // Codes mirror the sentence-builders above one for one: same condition,
    // machine-stable output. Kept alongside rather than derived from the
    // sentence, which names an item and can't be parsed back into a code.
    const oneAtATimeCode = () => single ? undefined : 'multiple_selected';
    const editReasonCode = () => readOnly ? 'read_only' : undefined;
    const capReasonCode = () => count > BULK_DELETE_MAX_IDS ? 'over_cap' : undefined;
    const firstNonNote = items.find((item) => !isNote(item));
    // "Deselect it to merge the notes" only helps when some notes are selected.
    const notNotesFix = items.some(isNote)
        ? `${firstNonNote ? describe(firstNonNote) : ''} Deselect it to merge the notes.`
        : `Select ${MERGE_MIN_NOTES} to ${MERGE_MAX_NOTES} notes to merge them.`;
    const mergeReason = {
        'not-notes': `Merge works on notes only. ${notNotesFix}`,
        'too-few': `Merge needs at least ${MERGE_MIN_NOTES} notes. Select another note to merge.`,
        'too-many': `You can merge up to ${MERGE_MAX_NOTES} notes at a time. Deselect ${count - MERGE_MAX_NOTES} to merge.`,
    };
    const mergeFailure = mergeRuleFailure(count, firstNonNote !== undefined);
    const firstFolder = items.find((item) => item.kind === 'folder');
    const firstNative = items.find((item) => item.kind === 'file' && !isUpload(item));
    let downloadReason;
    let downloadReasonCode;
    if (firstFolder) {
        downloadReason = `Download doesn't work on ${words.folders}s yet. ${describe(firstFolder)}${single ? '' : ' Deselect it to download the files.'}`;
        downloadReasonCode = 'unsupported_folder';
    }
    else if (firstNative) {
        downloadReason = `Download works on uploaded files. ${describe(firstNative)} Open it to export instead.`;
        downloadReasonCode = 'unsupported_native';
    }
    const mergeReasonCode = {
        'not-notes': 'merge_not_notes',
        'too-few': 'merge_too_few',
        'too-many': 'merge_too_many',
    };
    const reasons = {
        open: oneAtATime('open'),
        move: editReason('move') ?? capReason('move'),
        copy: capReason('copy'),
        // artifact.merge.service requires EDIT on every participant.
        merge: editReason('merge') ?? (mergeFailure ? mergeReason[mergeFailure] : undefined),
        favorite: undefined,
        download: downloadReason,
        delete: editReason('delete') ?? capReason('delete'),
        rename: oneAtATime('rename') ?? editReason('rename'),
    };
    const reasonCodes = {
        open: oneAtATimeCode(),
        move: editReasonCode() ?? capReasonCode(),
        copy: capReasonCode(),
        merge: editReasonCode() ?? (mergeFailure ? mergeReasonCode[mergeFailure] : undefined),
        favorite: undefined,
        download: downloadReasonCode,
        delete: editReasonCode() ?? capReasonCode(),
        rename: oneAtATimeCode() ?? editReasonCode(),
    };
    return ids.map((id) => {
        const reason = reasons[id];
        return reason
            ? { id, enabled: false, reason, reasonCode: reasonCodes[id] }
            : { id, enabled: true };
    });
}
//# sourceMappingURL=resolveSelectionActions.js.map